// libraries.
import { useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";

// store.
import { useRulesSlice } from "@store/apiSlices/incentiveRules/slices/rules";
import { useGetRulesToolsQuery } from "@store/apiSlices/incentiveRules/rulesTools.endpoints";
import { useUserProfile } from "@shared/GoogleLoginButton/user-slice/useUserProfile.hook";
import { useUpdateRuleMutation } from "@store/apiSlices/incentiveRules/editRules.endpoints";
import { useGetSubRutasQuery } from "@store/apiSlices/incentiveRules/subRutas.endpoints";

// interfaces.
import {
  RawAction,
  RawConditionOperator,
  RawRuleItem,
  RulesSchema,
} from "@store/apiSlices/incentiveRules/interfaces/rawRules.interfaces";

// ─── Form types ───────────────────────────────────────────────────────────────

export interface SubConditionValues {
  left: string;
  type: RawConditionOperator;
  right: string;
}

export interface ActionValues {
  actionName: string;
  tool: string;
  operation: string;
  output: string;
  params: Record<string, string>;
  actionConditionEnabled: boolean;
  actionConditionLeft: string;
  actionConditionType: RawConditionOperator;
  actionConditionRight: string;
}

export interface FormValues {
  rule_id: string;
  name: string;
  description: string;
  priority: number;
  is_active: boolean;
  sub_ruta_id: number;
  state_route: 1 | 2;
  conditionMode: "simple" | "and" | "or";
  conditionLeft: string;
  conditionType: RawConditionOperator;
  conditionRight: string;
  subConditions: SubConditionValues[];
  actions: ActionValues[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const parseValue = (v: string): string | number => {
  const n = Number(v);
  return v.trim() !== "" && !isNaN(n) ? n : v;
};

export const emptySubCondition: SubConditionValues = {
  left: "",
  type: "equals",
  right: "",
};

export const emptyAction: ActionValues = {
  actionName: "",
  tool: "",
  operation: "",
  output: "",
  params: {},
  actionConditionEnabled: false,
  actionConditionLeft: "",
  actionConditionType: "equals",
  actionConditionRight: "",
};

// ─── Edit-mode form populator ─────────────────────────────────────────────────

export const buildEditValues = (
  editRule: RawRuleItem,
  subRutaId: number,
  stateRoute: 1 | 2,
): Partial<FormValues> => {
  const rule = editRule.rule;
  const condition = rule.condition ?? null;
  const actions = rule.actions ?? [];
  const isComplex = condition != null && "conditions" in condition;

  const conditionBase = condition as { type: RawConditionOperator; left: string; right: unknown } | null;
  const conditionComplex = condition as {
    type: "and" | "or";
    conditions: { left: string; type: RawConditionOperator; right: unknown }[];
  } | null;

  return {
    rule_id: editRule.rule_id,
    name: rule.name,
    description: rule.description ?? "",
    priority: rule.priority,
    is_active: editRule.is_active === "active",
    sub_ruta_id: editRule.sub_ruta_id ?? subRutaId,
    state_route:
      editRule.state_route === 1 || editRule.state_route === 2
        ? editRule.state_route
        : stateRoute,
    conditionMode: isComplex ? (conditionComplex?.type ?? "and") : "simple",
    conditionLeft: !isComplex ? (conditionBase?.left ?? "") : "",
    conditionType: !isComplex ? (conditionBase?.type ?? "equals") : "equals",
    conditionRight: !isComplex ? String(conditionBase?.right ?? "") : "",
    subConditions: isComplex
      ? (conditionComplex?.conditions ?? []).map((c) => ({
          left: c.left,
          type: c.type,
          right: String(c.right ?? ""),
        }))
      : [emptySubCondition],
    actions:
      actions.length > 0
        ? actions.map((a) => ({
            actionName: a.name,
            tool: a.tool,
            operation: String(a.params.operation ?? a.params.type ?? ""),
            output: a.output,
            params: Object.fromEntries(
              Object.entries(a.params)
                .filter(([k]) => k !== "operation" && k !== "type")
                .map(([k, v]) => [k, String(v)]),
            ),
            actionConditionEnabled: !!a.condition,
            actionConditionLeft: a.condition?.left ?? "",
            actionConditionType: a.condition?.type ?? "equals",
            actionConditionRight: String(a.condition?.right ?? ""),
          }))
        : [emptyAction],
  };
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

interface Props {
  show: boolean;
  country: string;
  onClose: () => void;
  // Contexto del dashboard — requerido para asignar la regla a la sub-ruta
  subRutaId: number;
  stateRoute: 1 | 2;
  // Si se proporciona editRule, el modal está en modo editar
  editRule?: RawRuleItem | null;
}

export const useCreateRuleForm = ({
  show,
  country,
  onClose,
  subRutaId,
  stateRoute,
  editRule,
}: Props) => {
  const isEditMode = !!editRule;
  const { email } = useUserProfile();
  const { createRule, saveMetadata, isSaving } = useRulesSlice();
  const [updateRuleMutation] = useUpdateRuleMutation();

  const schema = country === "MX" ? "inventory_mx" : "inventory_co";

  const { data: toolsData, isLoading: isLoadingTools } = useGetRulesToolsQuery(
    undefined,
    { skip: !show },
  );
  const tools = toolsData?.tools ?? [];
  const contextVars = (toolsData?.contextColumns ?? []).map((col) => ({
    label: col.description,
    value: `\${${col.name}}`,
  }));

  const { data: subRutasOptions = [] } = useGetSubRutasQuery(
    { schema },
    { skip: !show },
  );

  const methods = useForm<FormValues>({
    defaultValues: {
      rule_id: "",
      name: "",
      description: "",
      priority: 10,
      is_active: true,
      sub_ruta_id: subRutaId,
      state_route: stateRoute,
      conditionMode: "simple",
      conditionLeft: "",
      conditionType: "equals",
      conditionRight: "",
      subConditions: [emptySubCondition],
      actions: [emptyAction],
    },
  });

  // Pre-poblar o limpiar el formulario al abrir/cerrar el modal.
  // En modo editar esperamos a que `tools` haya cargado para que los selects de
  // Tool y Operación tengan sus <option> disponibles en el momento del reset(),
  // de lo contrario el navegador no puede pre-seleccionar el valor guardado.
  useEffect(() => {
    if (!show) return;
    if (isEditMode && editRule) {
      if (isLoadingTools) return;
      methods.reset(buildEditValues(editRule, subRutaId, stateRoute));
    } else {
      methods.reset({
        rule_id: "", name: "", description: "", priority: 10, is_active: true,
        conditionMode: "simple", conditionLeft: "", conditionType: "equals",
        conditionRight: "", subConditions: [emptySubCondition], actions: [emptyAction],
        sub_ruta_id: subRutaId, state_route: stateRoute,
      });
    }
  }, [show, isEditMode, editRule, methods, subRutaId, stateRoute, isLoadingTools]);

  const subConditionArray = useFieldArray({
    control: methods.control,
    name: "subConditions",
  });

  const actionArray = useFieldArray({
    control: methods.control,
    name: "actions",
  });

  const onSubmit = methods.handleSubmit(async (data) => {
    const schema: RulesSchema = country === "MX" ? "inventory_mx" : "inventory_co";

    const condition =
      data.conditionMode === "simple"
        ? {
            left: data.conditionLeft,
            type: data.conditionType,
            right: parseValue(data.conditionRight),
          }
        : {
            type: data.conditionMode as "and" | "or",
            conditions: data.subConditions.map((c) => ({
              left: c.left,
              type: c.type,
              right: parseValue(c.right),
            })),
          };

    const actions: RawAction[] = data.actions.map(
      ({
        actionName,
        tool,
        operation,
        output,
        params,
        actionConditionEnabled,
        actionConditionLeft,
        actionConditionType,
        actionConditionRight,
      }) => {
        const operationKey = tool === "validacion" ? "type" : "operation";
        const builtAction: RawAction = {
          name: actionName,
          tool,
          output,
          params: {
            [operationKey]: operation,
            ...Object.fromEntries(
              Object.entries(params).map(([k, v]) => [k, parseValue(v)]),
            ),
          },
        };
        if (actionConditionEnabled && actionConditionLeft) {
          builtAction.condition = {
            left: actionConditionLeft,
            type: actionConditionType,
            right: parseValue(actionConditionRight),
          };
        }
        return builtAction;
      },
    );

    // En modo editar, `rule_id` está deshabilitado en el form y react-hook-form
    // devuelve undefined para campos disabled. Usamos editRule.rule_id directamente.
    const resolvedRuleId = isEditMode && editRule ? editRule.rule_id : data.rule_id;

    const rulePayload = {
      id: resolvedRuleId,
      name: data.name,
      description: data.description,
      priority: data.priority,
      condition,
      actions,
    };

    if (isEditMode && editRule) {
      // ── Modo editar: PUT /rules + PATCH /rules/metadata ─────────────────
      try {
        await updateRuleMutation({
          rule_id: resolvedRuleId,
          schema,
          is_active: data.is_active ? "active" : "inactive",
          changed_by: email,
          rule: rulePayload,
        }).unwrap();
      } catch (err) {
        console.error("[EditRule] Error al guardar cambios:", err);
        return;
      }
      // Actualiza sub_ruta_id y state_route si el usuario los cambió en el form.
      await saveMetadata({
        rule_id: resolvedRuleId,
        sub_ruta_id: data.sub_ruta_id,
        state_route: data.state_route,
        schema,
        changed_by: email,
      });
    } else {
      // ── Modo crear: POST /rules → PATCH /rules/metadata ─────────────────
      await createRule({
        rule_id: resolvedRuleId,
        schema,
        is_active: data.is_active ? "active" : "inactive",
        changed_by: email,
        rule: rulePayload,
      });

      // Asigna la regla recién creada a la sub-ruta y al state_route elegidos por el usuario
      await saveMetadata({
        rule_id: resolvedRuleId,
        sub_ruta_id: data.sub_ruta_id,
        state_route: data.state_route,
        schema,
        changed_by: email,
      });
    }

    methods.reset();
    onClose();
  });

  return {
    methods,
    onSubmit,
    isSaving,
    isEditMode,
    tools,
    contextVars,
    isLoadingTools,
    subConditionArray,
    actionArray,
    emptySubCondition,
    emptyAction,
    subRutasOptions,
  };
};
